// 파일경로: components/customer/RegisterForm.tsx
// 파일설명: Social 기관 가입에서 Identity 계정과 Social 기관 심사를 하나의 제출로 접수한다.
// 변경이력: 2026-11-02 R61. 기관 필수정보를 한 화면에서 수집하고 PENDING 접수 후에만 완료 표시.
// 변경이력: 2026-11-02 R59. 브라우저 복귀에 기관 전용 안내 힌트만 사용하고 공용 가입 API는 유지한다.
// 변경이력: 2026-11-02 R56. OAuth resume로 시작한 가입의 하단 로그인 재유도를 제거하고 가입 성공 후 앱 복귀 계약을 유지한다.
// 파일설명: Common Identity 회원가입 공통 양식. Social 기업·기관 가입의 명칭과 도움말만 유형에 맞게 표시한다.
// 개인 가입과 기존 가입 API, Identity OAuth 및 데이터 전송 필드의 동작은 유지한다.
// 변경이력: 2026-11-02 R55. 기업·기관 가입에서 담당자 개인 설명을 제거하고 기업·기관명 및 사이트 내 표시 이름으로 안내를 통일한다.
// 변경이력: 2026-11-02 R54. Social 기관 가입에서 성별 입력을 숨기고 현행 Identity 가입 API가 허용하는 기술적 기본값을 사용한다.
// 변경이력: 2026-11-02 R53. Social 기관 가입에는 창사일 입력을 표시하지 않고 필수 날짜의 유효 기본값을 전송한다. 개인 생년월일과 다른 서비스는 변경하지 않는다.
// 변경이력: 2026-11-02 R52. Social Desktop 개인·기관 가입 성공 시 전용 복귀 화면으로 연결하고 타 서비스 원래 동작은 유지한다.
// 변경이력: 2026-11-02 R51 가입화면 정정. Social 기관 가입에서 기존 날짜 입력을 창사일로 표시하며 인증 저장 계약은 유지한다.
// 변경이력: 2026-11-02 R51. 담당자 계정 생성 버튼의 과도한 기관 신청 완료 암시를 제거한다.
// 변경이력: 2026-11-01 R30. 개인 가입 기본 문구와 공통 인증 필드는 보존하고 기관 담당자 표시 문구만 선택 적용한다.

"use client";

import Link from "next/link";
import { FormEvent, useMemo, useState, useSyncExternalStore } from "react";

import {
  customerAuthorizationAfterSignupUrl,
  customerIdentityErrorMessage,
  registerCustomer,
  requestRegistrationEmailCode,
  verifyRegistrationEmailCode,
} from "@/lib/customer/identity-api";
import {
  ISO_REGION_CODES,
  SUPPORTED_CUSTOMER_LOCALES,
  browserPreferredLocale,
  regionDisplayName,
  regionFromLocale,
} from "@/lib/customer/global-profile-options";
import {
  DEFAULT_PHONE_REGION_CODE,
  composeE164PhoneNumber,
  formatPhoneNationalInput,
  phoneCountryOptions,
} from "@/lib/customer/phone-number";

type Props = {
  resume?: string;
  emailVerificationEnabled: boolean;
  phoneVerificationEnabled: boolean;
  registrationContext?: "social-organization-applicant";
};

function withResume(pathname: string, resume?: string): string {
  if (!resume) return pathname;
  const query = new URLSearchParams({ resume });
  return `${pathname}?${query.toString()}`;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STANDALONE_POST_REGISTRATION_URL = "/account";
const ORGANIZATION_ENROLL_URL = "/api/social/organization/enroll";
const ORGANIZATION_RECEIPT_STORAGE = "toofoo-social-org-application-receipt";
type OrganizationType = "COMPANY" | "NONPROFIT" | "PUBLIC_INSTITUTION" | "SCHOOL" | "OTHER";
const organizationTypes: readonly { value: OrganizationType; label: string }[] = [
  { value: "COMPANY", label: "기업" },
  { value: "NONPROFIT", label: "비영리단체" },
  { value: "PUBLIC_INSTITUTION", label: "공공기관" },
  { value: "SCHOOL", label: "학교·교육기관" },
  { value: "OTHER", label: "기타 기관" },
];
// Identity의 필수 DATE 필드는 0000-00-00을 받을 수 없다.
// Social 기관 가입에서만 날짜를 묻지 않고 서버가 허용하는 유효한 최소 날짜를 사용한다.
const SOCIAL_ORGANIZATION_UNSPECIFIED_BIRTH_DATE = "0001-01-01";
// 현행 Identity 가입 API의 sex는 MALE 또는 FEMALE만 허용한다.
// 기관 자체의 성별을 뜻하지 않는 기술적 대체값이며, 개인 가입에는 적용하지 않는다.
const SOCIAL_ORGANIZATION_DEFAULT_SEX = "MALE" as const;

const HYDRATION_SUBSCRIBE = () => () => {};
const HYDRATION_CLIENT_SNAPSHOT = () => true;
const HYDRATION_SERVER_SNAPSHOT = () => false;

export function RegisterForm({
  resume,
  emailVerificationEnabled,
  phoneVerificationEnabled,
  registrationContext,
}: Props) {
  const isOrganizationApplicant = registrationContext === "social-organization-applicant";
  const [legalName, setLegalName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [sex, setSex] = useState<"" | "MALE" | "FEMALE">("");
  const [displayName, setDisplayName] = useState("");
  const [homeRegionOverride, setHomeRegion] = useState<string | null>(null);
  const [preferredLocaleOverride, setPreferredLocale] = useState<string | null>(null);
  const [phoneRegionCode, setPhoneRegionCode] = useState(DEFAULT_PHONE_REGION_CODE);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [emailChallengeId, setEmailChallengeId] = useState("");
  const [emailVerificationCode, setEmailVerificationCode] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailMessageKind, setEmailMessageKind] = useState<"info" | "success" | "error">("info");
  const [requestingEmailCode, setRequestingEmailCode] = useState(false);
  const [verifyingEmailCode, setVerifyingEmailCode] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyNoticeAcknowledged, setPrivacyNoticeAcknowledged] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [organizationType, setOrganizationType] = useState<OrganizationType>("COMPANY");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [officialEmail, setOfficialEmail] = useState("");
  const [officialPhone, setOfficialPhone] = useState("");
  const [evidenceDescription, setEvidenceDescription] = useState("");
  const [applicationKey] = useState(() => crypto.randomUUID());
  const [organizationRetryToken, setOrganizationRetryToken] = useState<string | null>(null);
  const [applicationAccepted, setApplicationAccepted] = useState(false);

  const normalizedEmail = email.trim().toLowerCase();
  const emailVerificationBypass = !emailVerificationEnabled;
  const phoneVerificationBypass = !phoneVerificationEnabled;
  const emailVerified = emailVerificationBypass
    ? EMAIL_PATTERN.test(normalizedEmail)
    : Boolean(emailChallengeId)
      && Boolean(verifiedEmail)
      && verifiedEmail === normalizedEmail;

  const regionLabelsReady = useSyncExternalStore(
    HYDRATION_SUBSCRIBE,
    HYDRATION_CLIENT_SNAPSHOT,
    HYDRATION_SERVER_SNAPSHOT,
  );
  const detectedLocale = useSyncExternalStore(
    HYDRATION_SUBSCRIBE,
    browserPreferredLocale,
    () => "en-US",
  );
  const preferredLocale = preferredLocaleOverride ?? detectedLocale;
  const homeRegion = homeRegionOverride ?? regionFromLocale(detectedLocale);

  const regionOptions = useMemo(() => {
    if (!regionLabelsReady) {
      return ISO_REGION_CODES.map((code) => ({ code, label: code }));
    }
    const locale = preferredLocale || "en-US";
    return ISO_REGION_CODES
      .map((code) => ({
        code,
        label: regionDisplayName(code, locale),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [preferredLocale, regionLabelsReady]);

  const phoneOptions = useMemo(
    () => phoneCountryOptions("ko-KR", regionLabelsReady),
    [regionLabelsReady],
  );

  function resetEmailVerification() {
    setEmailChallengeId("");
    setEmailVerificationCode("");
    setVerifiedEmail("");
    setEmailMessage("");
    setEmailMessageKind("info");
  }

  async function requestEmailCode() {
    setErrorMessage("");
    setEmailMessage("");

    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setEmailMessageKind("error");
      setEmailMessage("올바른 이메일 주소를 입력해 주세요.");
      return;
    }

    setRequestingEmailCode(true);
    try {
      const result = await requestRegistrationEmailCode({
        email: normalizedEmail,
        preferred_locale: preferredLocale.trim() || "en-US",
        ...(emailChallengeId ? { challenge_id: emailChallengeId } : {}),
      });
      if (result.challenge_id) {
        setEmailChallengeId(result.challenge_id);
      }
      setVerifiedEmail("");
      setEmailVerificationCode("");
      setEmailMessageKind("info");
      setEmailMessage(
        result.queued
          ? "사용 가능한 이메일입니다. 인증코드를 보냈습니다."
          : `이미 인증코드를 요청했습니다. 약 ${result.resend_after_seconds}초 후 다시 받을 수 있습니다.`,
      );
    } catch (error) {
      setEmailChallengeId("");
      setVerifiedEmail("");
      setEmailVerificationCode("");
      setEmailMessageKind("error");
      setEmailMessage(
        customerIdentityErrorMessage(error, "registration-email"),
      );
    } finally {
      setRequestingEmailCode(false);
    }
  }

  async function verifyEmailCode() {
    setErrorMessage("");
    setEmailMessage("");

    if (!emailChallengeId) {
      setEmailMessageKind("error");
      setEmailMessage("먼저 이메일 인증번호를 받아 주세요.");
      return;
    }
    if (!/^[0-9]{6}$/.test(emailVerificationCode.trim())) {
      setEmailMessageKind("error");
      setEmailMessage("이메일로 받은 6자리 인증코드를 입력해 주세요.");
      return;
    }

    setVerifyingEmailCode(true);
    try {
      const result = await verifyRegistrationEmailCode({
        challenge_id: emailChallengeId,
        email: normalizedEmail,
        verification_code: emailVerificationCode.trim(),
      });
      setVerifiedEmail(result.email.toLowerCase());
      setEmailMessageKind("success");
      setEmailMessage("이메일 인증이 완료되었습니다.");
    } catch (error) {
      setVerifiedEmail("");
      setEmailMessageKind("error");
      setEmailMessage(
        customerIdentityErrorMessage(error, "registration-email"),
      );
    } finally {
      setVerifyingEmailCode(false);
    }
  }

  function handleOrganizationAccepted(receipt: string) {
    setApplicationAccepted(true);
    try {
      window.sessionStorage.setItem(ORGANIZATION_RECEIPT_STORAGE, receipt);
      window.sessionStorage.setItem("toofoo-social-org-return-hint", "1");
      window.location.assign(`/social/desktop/complete?${new URLSearchParams({ resume: resume ?? "" })}`);
    } catch {
      // Storage restrictions cannot erase the server-confirmed PENDING result.
    }
  }

  async function retryOrganizationIntake() {
    if (!organizationRetryToken || submitting) return;
    setSubmitting(true);
    setErrorMessage("");
    try {
      const response = await fetch(ORGANIZATION_ENROLL_URL, {
        method: "PUT", headers: { "Content-Type": "application/json" }, cache: "no-store",
        body: JSON.stringify({ kind: "retry", retryToken: organizationRetryToken }),
      });
      const data: unknown = await response.json().catch(() => null);
      if (response.status === 201 && data && typeof data === "object" &&
          (data as Record<string, unknown>).status === "PENDING" &&
          typeof (data as Record<string, unknown>).receipt === "string") {
        handleOrganizationAccepted((data as Record<string, string>).receipt);
      } else {
        setErrorMessage("기관 심사 접수가 확인되지 않았습니다. 잠시 후 같은 접수 재시도를 이용해 주세요.");
      }
    } catch { setErrorMessage("연결을 확인할 수 없습니다. 같은 접수 재시도를 이용해 주세요."); }
    finally { setSubmitting(false); }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage("");

    if (!legalName.trim()) {
      setErrorMessage(isOrganizationApplicant ? "기업·기관명을 입력해 주세요." : "성명을 입력해 주세요.");
      return;
    }

    if (!isOrganizationApplicant && !birthDate) {
      setErrorMessage("생년월일을 입력해 주세요.");
      return;
    }

    const selectedSex = isOrganizationApplicant ? SOCIAL_ORGANIZATION_DEFAULT_SEX : sex;
    if (!selectedSex) {
      setErrorMessage("성별을 선택해 주세요.");
      return;
    }

    if (!displayName.trim()) {
      setErrorMessage(isOrganizationApplicant ? "사이트 내 표시 이름을 입력해 주세요." : "사이트 내 표시이름(닉네임)을 입력해 주세요.");
      return;
    }

    if (!homeRegion) {
      setErrorMessage("국가/지역을 선택해 주세요.");
      return;
    }

    const normalizedPhone = composeE164PhoneNumber(phoneRegionCode, phoneNumber);
    if (!normalizedPhone) {
      setErrorMessage("휴대폰 국가번호를 확인하고 나머지 번호를 입력해 주세요.");
      return;
    }

    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setErrorMessage("올바른 이메일 주소를 입력해 주세요.");
      return;
    }

    if (emailVerificationEnabled && (!emailVerified || !emailChallengeId)) {
      setErrorMessage("회원가입 전에 이메일 인증을 완료해 주세요.");
      setEmailMessageKind("error");
      setEmailMessage("이메일 오른쪽의 인증번호 받기를 누르고 6자리 코드를 확인해 주세요.");
      return;
    }

    if (password !== passwordConfirm) {
      setErrorMessage("비밀번호가 서로 일치하지 않습니다.");
      return;
    }

    if (password.length < 8) {
      setErrorMessage("비밀번호는 8자 이상 입력해 주세요.");
      return;
    }

    if (!termsAccepted) {
      setErrorMessage("회원가입을 위해 이용약관에 동의해 주세요.");
      return;
    }

    if (!privacyNoticeAcknowledged) {
      setErrorMessage("회원가입을 위해 개인정보 처리방침을 확인해 주세요.");
      return;
    }

    if (isOrganizationApplicant && organizationRetryToken) {
      setErrorMessage("계정이 생성되어 심사 접수만 재시도할 수 있습니다. 아래의 접수 재시도를 눌러 주세요.");
      return;
    }
    if (isOrganizationApplicant && (!registrationNumber.trim() || !officialEmail.trim() ||
       !officialPhone.trim() || evidenceDescription.trim().length < 12)) {
      setErrorMessage("기업·기관 등록번호, 공식 연락처 및 증빙 설명을 모두 입력해 주세요.");
      return;
    }
    setSubmitting(true);
    try {
      const identityInput = {
        ...(resume ? { resume } : {}),
        ...(emailVerificationBypass
          ? {}
          : { email_verification_challenge_id: emailChallengeId }),
        legal_name: legalName.trim(),
        birth_date: isOrganizationApplicant
          ? SOCIAL_ORGANIZATION_UNSPECIFIED_BIRTH_DATE
          : birthDate,
        sex: selectedSex,
        display_name: displayName.trim(),
        preferred_locale: preferredLocale.trim() || "en-US",
        home_region: homeRegion,
        phone_number: normalizedPhone,
        phone_region_code: phoneRegionCode,
        email: normalizedEmail,
        password,
        terms_accepted: termsAccepted,
        privacy_notice_acknowledged: privacyNoticeAcknowledged,
        marketing_consent: marketingConsent,
      };
      if (isOrganizationApplicant) {
        const application = {
          idempotency_key: applicationKey,
          official_name: legalName.trim(),
          organization_type: organizationType,
          registration_number: registrationNumber.trim(),
          contact_email: officialEmail.trim(),
          contact_phone: officialPhone.trim(),
          evidence_description: evidenceDescription.trim(),
        };
        const response = await fetch(ORGANIZATION_ENROLL_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ kind: "register", identity: identityInput, application }),
          cache: "no-store",
        });
        const result: unknown = await response.json().catch(() => null);
        if (!result || typeof result !== "object") throw Error("기관 심사 신청을 확인하지 못했습니다.");
        const row = result as Record<string, unknown>;
        if (response.status === 503 && row.code === "ORG_SOCIAL_INTAKE_RETRY" && typeof row.retryToken === "string") {
          setOrganizationRetryToken(row.retryToken);
          setErrorMessage("계정은 생성되었으나 기관 심사 접수를 확인하지 못했습니다. 기존 계정으로 접수만 재시도해 주세요.");
          return;
        }
        if (response.status !== 201 || row.status !== "PENDING" || typeof row.receipt !== "string") {
          setErrorMessage("기관 가입 신청이 접수되지 않았습니다. 입력 정보를 확인해 주세요. 계정 생성 여부가 불명확하면 중복 가입하지 마세요.");
          return;
        }
        handleOrganizationAccepted(row.receipt);
        return;
      }
      await registerCustomer(identityInput);
      // An unrelated old institution UI hint cannot relabel a personal signup.
      try {
        window.sessionStorage.removeItem("toofoo-social-org-return-hint");
        window.sessionStorage.removeItem(ORGANIZATION_RECEIPT_STORAGE);
      } catch { /* Session storage is not needed for personal OAuth. */ }
      if (resume) {
        window.location.assign(await customerAuthorizationAfterSignupUrl(resume));
        return;
      }
      window.location.assign(STANDALONE_POST_REGISTRATION_URL);
    } catch (error) {
      setErrorMessage(customerIdentityErrorMessage(error, "register"));
      if (
        customerIdentityErrorMessage(error, "register").includes("이메일")
      ) {
        resetEmailVerification();
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (applicationAccepted) return (
    <section className="auth-card" data-e2e="social-organization-application-confirmed" role="status">
      <h2>기관 심사 신청이 접수되었습니다.</h2>
      <p>관리자 심사는 24시간 내에 처리되며, 승인 완료 후 이용할 수 있습니다.</p>
      <p>승인 전에는 기관 서비스를 이용할 수 없습니다.</p>
    </section>
  );

  return (
    <form
      className="auth-form registration-form"
      onSubmit={submit}
      data-e2e="customer-register-form"
      data-email-verification-enabled={emailVerificationEnabled ? "true" : "false"}
      data-phone-verification-enabled={phoneVerificationEnabled ? "true" : "false"}
    >
      <section
        className="registration-section registration-section--identity"
        data-e2e="customer-registration-primary-identity"
      >
        <div className="registration-section-heading">
          <div>
            <p className="registration-section-kicker">{isOrganizationApplicant ? "기업·기관 기본 정보" : "기본 정보"}</p>
            <h2>{isOrganizationApplicant ? "기업·기관명과 표시 이름을 입력해 주세요" : "성명과 사이트 내 표시이름을 설정하세요"}</h2>
            <p>{isOrganizationApplicant ? "기업·기관명과 사이트에서 보여줄 이름을 입력해 주세요. 기업·기관 정보를 함께 제출하면 관리자 심사가 바로 접수됩니다." : "성명은 계정 기준 정보이고, 사이트 내 표시이름(닉네임)은 TOOFOO 서비스에서 보이는 이름입니다."}</p>
          </div>
          <span className="registration-section-badge">필수</span>
        </div>

        <div className="registration-grid registration-grid--2">
          <div className="form-field form-field--featured">
            <label htmlFor="legal-name">{isOrganizationApplicant ? "기업·기관명(실명)" : "성명"}</label>
            <input
              id="legal-name"
              name="legal-name"
              autoComplete={isOrganizationApplicant ? "organization" : "name"}
              value={legalName}
              onChange={(event) => {
                const next = event.target.value;
                setDisplayName((current) => (!current || current === legalName ? next : current));
                setLegalName(next);
              }}
              placeholder={isOrganizationApplicant ? "기업·기관의 공식 명칭" : "본인의 성명"}
              required
              maxLength={160}
            />
            <p className="field-help">{isOrganizationApplicant ? "등록할 기업·기관의 공식 명칭을 입력해 주세요." : "현재는 직접 입력하며 이후 계정에서 수정할 수 있습니다."}</p>
          </div>

          <div className="form-field form-field--featured">
            <label htmlFor="display-name">{isOrganizationApplicant ? "사이트 내 표시 이름" : "사이트 내 표시이름(닉네임)"}</label>
            <input
              id="display-name"
              name="display-name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder={isOrganizationApplicant ? "사이트에 표시할 기업·기관 이름" : "사이트에서 사용할 이름"}
              required
              maxLength={160}
            />
            <p className="field-help">{isOrganizationApplicant ? "기업·기관명으로 자동 채워지며 원하는 표시 이름으로 변경할 수 있습니다." : "처음에는 성명을 따라 채워지며 닉네임이나 활동명으로 바꿀 수 있습니다."}</p>
          </div>
        </div>
      </section>

      <section
        className="registration-section registration-section--verification"
        data-e2e="customer-registration-contact-verification"
      >
        <div className="registration-section-heading">
          <div>
            <p className="registration-section-kicker">{isOrganizationApplicant ? "기업·기관 연락처 확인" : "연락처 확인"}</p>
            <h2>{isOrganizationApplicant ? "가입에 사용할 이메일과 연락처를 확인해 주세요" : "사용 중인 연락처를 확인합니다"}</h2>
            <p>{isOrganizationApplicant ? "기업·기관 가입에 사용할 이메일과 연락처를 입력해 주세요." : "가입과 계정 확인에 사용할 이메일과 휴대폰 번호를 입력해 주세요."}</p>
          </div>
          <span className={`registration-section-badge ${emailVerified ? "is-complete" : ""}`}>
            {emailVerificationBypass
              ? "이메일 DEV 인증 생략"
              : emailVerified
                ? "이메일 확인 완료"
                : "이메일 확인 필요"}
          </span>
        </div>

        <div
          className="verification-card"
          data-e2e="customer-registration-email-verification"
        >
          <div className="verification-card-heading">
            <div>
              <label htmlFor="email">이메일</label>
              <p>
                {emailVerificationBypass
                  ? "DEV 테스트 환경에서는 이메일 인증을 생략합니다."
                  : "인증번호 받기를 누르면 중복확인 후 6자리 코드를 발송합니다."}
              </p>
            </div>
            <span className={`verification-status-chip ${emailVerified ? "is-verified" : "is-pending"}`}>
              {emailVerificationBypass
                ? "DEV 인증 생략"
                : emailVerified
                  ? "인증 완료"
                  : "인증 필요"}
            </span>
          </div>

          <div className="verification-field-row">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                resetEmailVerification();
              }}
              placeholder="name@example.com"
              required
              maxLength={320}
              readOnly={emailVerificationEnabled && emailVerified}
            />
            {emailVerificationBypass ? null : (
              <button
                className="secondary-button verification-action-button"
                type="button"
                onClick={requestEmailCode}
                disabled={requestingEmailCode || emailVerified}
                data-e2e="customer-registration-email-code-request"
              >
                {emailVerified
                  ? "인증 완료"
                  : requestingEmailCode
                    ? "확인 중..."
                    : emailChallengeId
                      ? "인증번호 다시 받기"
                      : "인증번호 받기"}
              </button>
            )}
          </div>

          {!emailVerificationBypass && emailChallengeId && !emailVerified ? (
            <div
              className="verification-code-panel verification-code-panel--inline"
              data-e2e="customer-registration-email-code-panel"
            >
              <div className="verification-code-copy">
                <strong>인증번호 입력</strong>
                <span>메일로 받은 6자리 숫자를 입력해 주세요.</span>
              </div>
              <div className="verification-field-row">
                <input
                  id="registration-email-code"
                  name="registration-email-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={emailVerificationCode}
                  onChange={(event) => {
                    setEmailVerificationCode(
                      event.target.value.replace(/[^0-9]/g, "").slice(0, 6),
                    );
                  }}
                  placeholder="000000"
                  maxLength={6}
                  aria-label="이메일 인증번호"
                />
                <button
                  className="secondary-button verification-action-button"
                  type="button"
                  onClick={verifyEmailCode}
                  disabled={verifyingEmailCode}
                  data-e2e="customer-registration-email-code-verify"
                >
                  {verifyingEmailCode ? "확인 중..." : "인증 확인"}
                </button>
              </div>
            </div>
          ) : null}

          {emailMessage ? (
            <p
              className={`verification-message is-${emailMessageKind}`}
              role={emailMessageKind === "error" ? "alert" : "status"}
              data-e2e="customer-registration-email-message"
            >
              {emailMessage}
            </p>
          ) : null}
        </div>

        <div
          className="verification-card verification-card--disabled"
          data-e2e="customer-registration-phone-verification"
        >
          <div className="verification-card-heading">
            <div>
              <label htmlFor="phone-number">휴대폰 번호</label>
              <p>대한민국 번호는 010부터 입력하면 됩니다.</p>
            </div>
            <span className="verification-status-chip is-disabled">
              {phoneVerificationBypass ? "DEV 인증 생략" : "휴대폰 번호 입력"}
            </span>
          </div>

          <div className="phone-entry-row" data-e2e="customer-registration-phone-entry">
            <div className="phone-country-control">
              <label className="sr-only" htmlFor="phone-country-code">휴대폰 국가번호</label>
              <select
                id="phone-country-code"
                name="phone-country-code"
                value={phoneRegionCode}
                onChange={(event) => {
                  const nextRegion = event.target.value;
                  setPhoneRegionCode(nextRegion);
                  setPhoneNumber((current) => formatPhoneNationalInput(nextRegion, current));
                }}
                aria-label="휴대폰 국가번호"
                required
                data-e2e="customer-registration-phone-country-code"
              >
                {phoneOptions.map((item) => (
                  <option key={item.code} value={item.code}>{item.label}</option>
                ))}
              </select>
            </div>

            <div className="phone-national-control">
              <input
                id="phone-number"
                name="phone-number"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(formatPhoneNationalInput(phoneRegionCode, event.target.value))}
                placeholder={phoneRegionCode === "KR" ? "010 1234 5678" : "휴대폰 번호"}
                aria-label="휴대폰 번호"
                required
                maxLength={24}
              />
            </div>

            <button
              className="secondary-button verification-action-button phone-verification-button"
              type="button"
              disabled
              aria-disabled="true"
              data-e2e="customer-registration-phone-code-request"
            >
              {phoneVerificationBypass ? "DEV 인증 생략" : "본인인증"}
            </button>
          </div>
          <p
            className="field-help"
            data-e2e="customer-registration-phone-verification-status"
          >
            {phoneVerificationBypass
              ? "DEV 테스트 환경에서는 휴대폰 본인인증을 생략합니다. 실제 테스트에 사용할 번호를 입력해 주세요."
              : "대한민국 번호는 국가번호를 그대로 두고 010부터 입력해 주세요. 다른 나라 번호는 국가번호를 먼저 선택한 뒤 현지 번호를 입력해 주세요."}
          </p>
        </div>
      </section>

      <section className="registration-section" data-e2e="customer-registration-security">
        <div className="registration-section-heading">
          <div>
            <p className="registration-section-kicker">{isOrganizationApplicant ? "기업·기관 계정 보안" : "계정 보안"}</p>
            <h2>비밀번호를 설정하세요</h2>
            <p>비밀번호는 8자 이상 입력해 주세요.</p>
          </div>
          <span className="registration-section-badge">필수</span>
        </div>

        <div className="registration-grid registration-grid--2">
          <div className="form-field">
            <label htmlFor="password">비밀번호</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="8자 이상 입력"
              required
              minLength={8}
              maxLength={72}
            />
          </div>

          <div className="form-field">
            <label htmlFor="password-confirm">비밀번호 확인</label>
            <input
              id="password-confirm"
              name="password-confirm"
              type="password"
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(event) => setPasswordConfirm(event.target.value)}
              placeholder="비밀번호를 다시 입력"
              required
              minLength={8}
              maxLength={72}
            />
          </div>
        </div>
      </section>

      {isOrganizationApplicant ? (
        <section className="registration-section" data-e2e="social-organization-one-step-application">
          <div className="registration-section-heading"><div>
            <p className="registration-section-kicker">관리자 심사 신청 정보</p>
            <h2>기업·기관 등록 정보를 함께 제출해 주세요</h2>
            <p>가입 버튼 한 번으로 계정 생성과 관리자 심사 신청이 함께 접수됩니다. 따로 로그인하거나 다시 신청할 필요가 없습니다.</p>
          </div><span className="registration-section-badge">필수</span></div>
          <div className="registration-grid registration-grid--2">
            <div className="form-field"><label htmlFor="organization-type">기업·기관 유형</label>
              <select id="organization-type" value={organizationType} onChange={(e) => setOrganizationType(e.target.value as OrganizationType)}>
                {organizationTypes.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </select></div>
            <div className="form-field"><label htmlFor="registration-number">사업자·기관 등록번호</label>
              <input id="registration-number" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} required minLength={5} maxLength={40} /></div>
            <div className="form-field"><label htmlFor="official-email">기업·기관 공식 이메일</label>
              <input id="official-email" type="email" value={officialEmail} onChange={(e) => setOfficialEmail(e.target.value)} required maxLength={254} /></div>
            <div className="form-field"><label htmlFor="official-phone">기업·기관 대표 연락처</label>
              <input id="official-phone" value={officialPhone} onChange={(e) => setOfficialPhone(e.target.value)} required minLength={7} maxLength={32} /></div>
          </div>
          <div className="form-field"><label htmlFor="organization-evidence">실체 확인 자료 설명</label>
            <textarea id="organization-evidence" value={evidenceDescription} onChange={(e) => setEvidenceDescription(e.target.value)} rows={3} required minLength={12} maxLength={400} /></div>
        </section>
      ) : null}

      <section
        className="registration-section registration-section--additional"
        data-e2e="customer-registration-additional-profile"
      >
        <div className="registration-section-heading">
          <div>
            <p className="registration-section-kicker">{isOrganizationApplicant ? "기업·기관 기본 설정" : "추가 정보"}</p>
            <h2>{isOrganizationApplicant ? "국가와 언어를 선택해 주세요" : "계정 기본 정보를 확인하세요"}</h2>
            <p>{isOrganizationApplicant ? "계정 안내에 사용할 국가와 기본 언어를 선택해 주세요." : "서비스 안내와 계정 운영에 필요한 기본 정보입니다."}</p>
          </div>
          <span className="registration-section-badge">필수</span>
        </div>

        <div className="registration-additional-grid">
          {!isOrganizationApplicant && (
            <div className="form-field">
              <label htmlFor="birth-date">생년월일</label>
              <input
                id="birth-date"
                name="birth-date"
                type="date"
                autoComplete="bday"
                value={birthDate}
                onChange={(event) => setBirthDate(event.target.value)}
                required
              />
              <p className="field-help">주민등록번호는 받지 않고 생년월일만 저장합니다.</p>
            </div>
          )}

          {!isOrganizationApplicant && (
            <div className="form-field">
              <label htmlFor="sex">성별</label>
              <select
                id="sex"
                name="sex"
                value={sex}
                onChange={(event) => setSex(event.target.value as "" | "MALE" | "FEMALE")}
                required
              >
                <option value="">선택</option>
                <option value="MALE">남성</option>
                <option value="FEMALE">여성</option>
              </select>
            </div>
          )}

          <div className="form-field">
            <label htmlFor="home-region">국가/지역</label>
            <select
              id="home-region"
              name="home-region"
              value={homeRegion}
              onChange={(event) => setHomeRegion(event.target.value)}
              required
            >
              <option value="">선택</option>
              {regionOptions.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label} ({item.code})
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="preferred-locale">기본 언어</label>
            <select
              id="preferred-locale"
              name="preferred-locale"
              value={preferredLocale}
              onChange={(event) => setPreferredLocale(event.target.value)}
              required
            >
              {SUPPORTED_CUSTOMER_LOCALES.map((item) => (
                <option key={item.code} value={item.code}>{item.label}</option>
              ))}
            </select>
            <p className="field-help">계정 안내와 이메일에 사용할 기본 언어입니다.</p>
          </div>
        </div>
      </section>

      <section
        className="registration-section registration-section--policies"
        data-e2e="customer-registration-policy-consent"
      >
        <div className="registration-section-heading">
          <div>
            <p className="registration-section-kicker">약관 동의</p>
            <h2>가입에 필요한 내용을 확인해 주세요</h2>
            <p>필수 항목은 회원가입을 위해 필요하며, 마케팅 수신은 선택할 수 있습니다.</p>
          </div>
          <span className="registration-section-badge">필수 2 · 선택 1</span>
        </div>

        <div className="registration-policy-list">
          <label className="registration-policy-row">
            <input
              id="terms-accepted"
              type="checkbox"
              checked={termsAccepted}
              onChange={(event) => setTermsAccepted(event.target.checked)}
              required
              data-e2e="customer-registration-terms-accepted"
            />
            <span className="registration-policy-check" aria-hidden="true" />
            <span className="registration-policy-copy">
              <strong><em>필수</em> 이용약관에 동의합니다.</strong>
              <small>서비스 이용조건과 회원의 권리·의무를 확인합니다.</small>
            </span>
            <a
              className="registration-policy-link"
              href="https://www.toofoo.co.kr/terms"
              target="_blank"
              rel="noreferrer"
            >
              자세히 보기
            </a>
          </label>

          <label className="registration-policy-row">
            <input
              id="privacy-notice-acknowledged"
              type="checkbox"
              checked={privacyNoticeAcknowledged}
              onChange={(event) => setPrivacyNoticeAcknowledged(event.target.checked)}
              required
              data-e2e="customer-registration-privacy-acknowledged"
            />
            <span className="registration-policy-check" aria-hidden="true" />
            <span className="registration-policy-copy">
              <strong><em>필수</em> 개인정보 처리방침을 확인했습니다.</strong>
              <small>회원가입과 서비스 제공을 위한 개인정보 처리 내용을 확인합니다.</small>
            </span>
            <a
              className="registration-policy-link"
              href="https://www.toofoo.co.kr/privacy"
              target="_blank"
              rel="noreferrer"
            >
              자세히 보기
            </a>
          </label>

          <label className="registration-policy-row">
            <input
              id="marketing-consent"
              type="checkbox"
              checked={marketingConsent}
              onChange={(event) => setMarketingConsent(event.target.checked)}
              data-e2e="customer-registration-marketing-consent"
            />
            <span className="registration-policy-check" aria-hidden="true" />
            <span className="registration-policy-copy">
              <strong><em className="is-optional">선택</em> 마케팅 정보 수신에 동의합니다.</strong>
              <small>이메일·SMS로 이벤트, 혜택, 서비스 소식을 받을 수 있습니다.</small>
            </span>
            <a
              className="registration-policy-link"
              href="https://www.toofoo.co.kr/marketing-consent"
              target="_blank"
              rel="noreferrer"
            >
              자세히 보기
            </a>
          </label>
        </div>
      </section>

      {errorMessage ? (
        <div className="form-error" role="alert" data-e2e="customer-register-error">
          {errorMessage}
        </div>
      ) : null}

      <div className="registration-submit-panel">
        <div className="registration-submit-copy">
          <strong>{isOrganizationApplicant ? "기업·기관 가입 준비 상태" : "가입 준비 상태"}</strong>
          <span className={emailVerified ? "is-ready" : "is-required"}>
            {emailVerificationBypass
              ? "이메일 DEV 인증 생략"
              : `이메일 ${emailVerified ? "인증 완료" : "인증 필요"}`}
          </span>
          <span className="is-muted">
            {phoneVerificationBypass ? "휴대폰 DEV 인증 생략" : "휴대폰 번호 입력"}
          </span>
        </div>
        <button className="primary-button registration-submit-button" type="submit" disabled={submitting || !!organizationRetryToken}>
          {submitting ? "가입 처리 중..." : isOrganizationApplicant ? "기업·기관 가입 및 심사 신청" : "회원가입"}
        </button>
      </div>

      {isOrganizationApplicant && organizationRetryToken ? (
        <button type="button" className="primary-button" onClick={() => void retryOrganizationIntake()} disabled={submitting}>
          {submitting ? "접수 확인 중..." : "기존 가입의 심사 접수 재시도"}
        </button>
      ) : null}

      {!resume ? (
        <p className="auth-switch">
          이미 계정이 있나요? <Link href="/login">로그인</Link>
        </p>
      ) : null}
    </form>
  );
}
